import { useEffect, useState, useRef } from "react";
import { db } from "../firebase";
import {
  collection,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  setDoc,
} from "firebase/firestore";
import { getAuth } from "firebase/auth";
import {
  Container,
  Tabs,
  Tab,
  Form,
  Button,
  Card,
  Table,
  Alert,
  Row,
  Col,
} from "react-bootstrap";
import "./AdminDashboard.css";
import AuthGuard from "./AuthGuard";
import {
  formatRecurrenceInfo,
  weekDays,
  formatDateGMT7,
  checkTimeSlotAvailability,
} from "../utils";
import ConfirmDeleteModal from "./modals/ConfirmDeleteModal";
import Flatpickr from "flatpickr";
import "flatpickr/dist/flatpickr.min.css";
import { format, isValid } from "date-fns";
import staticAllowedEmails from "../data/staticAllowedEmails.json";
import { sendEmailToSingleEmail } from "../emailUtils";
import BookingEmailTemplate from "./BookingEmailTemplate";
import { getAdminEmails } from "../data/getAdminEmails";
import moment from "moment-timezone";

const sendBookingEmails = async (
  booking,
  action = "created",
  adminEmail = ""
) => {
  const subjectSuffix =
    action === "updated"
      ? "Update"
      : action === "deleted"
      ? "Cancellation"
      : "Confirmation";

  const subject = `Booking ${subjectSuffix} for ${booking.room}`;
  const emailContent = BookingEmailTemplate({
    booking: {
      ...booking,
      updated: action === "updated",
      cancelled: action === "deleted",
      room: { name: booking.room }, // ensure shape matches template
    },
    adminEmail,
  });

  // Send to booker
  await sendEmailToSingleEmail(
    booking.email || "noreply@kambria.io",
    subject,
    emailContent,
    booking.name || "Admin"
  );

  // Send to admins
  const adminEmails = await getAdminEmails();
  await Promise.all(
    adminEmails.map((adminEmail) =>
      sendEmailToSingleEmail(adminEmail, subject, emailContent, "Admin")
    )
  );
};

const AdminDashboard = () => {
  const [bookings, setBookings] = useState([]);
  const [newBooking, setNewBooking] = useState({
    room: "",
    date: "",
    startTime: "",
    endTime: "",
    name: "",
    purpose: "",
    isRecurring: false,
    recurrenceType: "weekly",
    recurrenceEndDate: "",
    recurrenceDays: [],
  });
  const [editingBooking, setEditingBooking] = useState(null);
  const [rooms, setRooms] = useState([]);
  const [newRoom, setNewRoom] = useState("");
  const [editingRoomId, setEditingRoomId] = useState(null);
  const [editedRoomName, setEditedRoomName] = useState("");
  const [activeTab, setActiveTab] = useState("rooms");
  const [notification, setNotification] = useState(null);
  // Modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [itemType, setItemType] = useState("");
  const [itemName, setItemName] = useState("");
  // Loading states for form submissions
  const [isCreatingBooking, setIsCreatingBooking] = useState(false);
  const [isUpdatingBooking, setIsUpdatingBooking] = useState(false);
  // Booking counts state
  const [bookingCounts, setBookingCounts] = useState({});
  // User management state
  const [allowedEmails, setAllowedEmails] = useState([]);
  const [newEmail, setNewEmail] = useState("");
  const [isAddingEmail, setIsAddingEmail] = useState(false);

  // Flatpickr refs for new booking
  const newDatePickerRef = useRef(null);
  const newStartTimePickerRef = useRef(null);
  const newEndTimePickerRef = useRef(null);
  const newRecurrenceEndDatePickerRef = useRef(null);

  // Flatpickr refs for editing booking
  const editDatePickerRef = useRef(null);
  const editStartTimePickerRef = useRef(null);
  const editEndTimePickerRef = useRef(null);
  const editRecurrenceEndDatePickerRef = useRef(null);

  const auth = getAuth();
  const currentAdminEmail = auth.currentUser?.email || "unknown@kambria.io";

  const showNotification = (message, variant = "success") => {
    setNotification({ message, variant });
    setTimeout(() => setNotification(null), 3000);
  };

  const countBookings = async (roomId) => {
    try {
      const bookingsRef = collection(db, "rooms", roomId, "bookings");
      const snapshot = await getDocs(bookingsRef);
      return snapshot.size;
    } catch (err) {
      console.error("Failed to fetch bookings for room", roomId, ":", err);
      return 0;
    }
  };

  const fetchRooms = async () => {
    try {
      const snap = await getDocs(collection(db, "rooms"));
      const roomsData = snap.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
      setRooms(roomsData);

      const counts = {};
      for (const room of roomsData) {
        counts[room.id] = await countBookings(room.id);
      }
      setBookingCounts(counts);
    } catch (error) {
      console.error("Error fetching rooms:", error);
      showNotification("Error fetching rooms", "danger");
    }
  };

  const fetchBookings = async () => {
    try {
      const roomsSnapshot = await getDocs(collection(db, "rooms"));
      const roomsData = roomsSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      const allBookings = [];
      for (const room of roomsData) {
        const bookingsRef = collection(db, "rooms", room.id, "bookings");
        const bookingsSnapshot = await getDocs(bookingsRef);
        const roomBookings = bookingsSnapshot.docs.map((doc) => ({
          id: doc.id,
          room: room.name,
          roomId: room.id,
          ...doc.data(),
        }));
        allBookings.push(...roomBookings);
      }

      setBookings(allBookings);
    } catch (error) {
      console.error("Error fetching bookings:", error);
      showNotification("Error fetching bookings", "danger");
    }
  };

  const fetchAllowedEmails = async () => {
    try {
      const emailsSnapshot = await getDocs(collection(db, "allowedEmails"));
      const emails = emailsSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setAllowedEmails(emails);
    } catch (error) {
      console.error("Error fetching allowed emails:", error);
      showNotification("Error fetching allowed emails", "danger");
    }
  };

  useEffect(() => {
    fetchBookings();
    fetchRooms();
    fetchAllowedEmails(); // eslint-disable-next-line
  }, []);

  // Initialize Flatpickr for new booking form
  useEffect(() => {
    if (newDatePickerRef.current) {
      Flatpickr(newDatePickerRef.current, {
        dateFormat: "Y-m-d",
        minDate: "today",
        onChange: ([selectedDate]) => {
          if (selectedDate) {
            setNewBooking((prev) => ({
              ...prev,
              date: formatDateGMT7(selectedDate),
            }));
          }
        },
      });
    }

    let newRecurrenceEndDatePicker = null;
    if (newBooking.isRecurring && newRecurrenceEndDatePickerRef.current) {
      const minDate = newBooking.date ? new Date(newBooking.date) : new Date();
      minDate.setDate(minDate.getDate() + 1);

      newRecurrenceEndDatePicker = Flatpickr(
        newRecurrenceEndDatePickerRef.current,
        {
          dateFormat: "Y-m-d",
          minDate: formatDateGMT7(minDate),
          onChange: ([selectedDate]) => {
            if (selectedDate) {
              setNewBooking((prev) => ({
                ...prev,
                recurrenceEndDate: formatDateGMT7(selectedDate),
              }));
            }
          },
        }
      );
    }

    if (newStartTimePickerRef.current) {
      Flatpickr(newStartTimePickerRef.current, {
        enableTime: true,
        noCalendar: true,
        dateFormat: "H:i",
        time_24hr: true,
        onChange: ([selectedTime]) => {
          if (selectedTime) {
            const timeStr = selectedTime.toTimeString().slice(0, 5);
            setNewBooking((prev) => ({ ...prev, startTime: timeStr }));
          }
        },
      });
    }

    if (newEndTimePickerRef.current) {
      Flatpickr(newEndTimePickerRef.current, {
        enableTime: true,
        noCalendar: true,
        dateFormat: "H:i",
        time_24hr: true,
        onChange: ([selectedTime]) => {
          if (selectedTime) {
            const timeStr = selectedTime.toTimeString().slice(0, 5);
            setNewBooking((prev) => ({ ...prev, endTime: timeStr }));
          }
        },
      });
    }

    return () => {
      if (newRecurrenceEndDatePicker) {
        newRecurrenceEndDatePicker.destroy();
      }
    }; //eslint-disable-next-line
  }, [newBooking.room, newBooking.isRecurring]);

  // Initialize Flatpickr for edit booking form
  useEffect(() => {
    if (editDatePickerRef.current && editingBooking) {
      Flatpickr(editDatePickerRef.current, {
        dateFormat: "Y-m-d",
        minDate: "today",
        defaultDate: editingBooking.date,
        onChange: ([selectedDate]) => {
          if (selectedDate) {
            setEditingBooking((prev) => ({
              ...prev,
              date: formatDateGMT7(selectedDate),
            }));
          }
        },
      });
    }

    let editRecurrenceEndDatePicker = null;
    if (
      editingBooking &&
      editingBooking.isRecurring &&
      editRecurrenceEndDatePickerRef.current
    ) {
      const minDate = editingBooking.date
        ? new Date(editingBooking.date)
        : new Date();
      minDate.setDate(minDate.getDate() + 1);

      editRecurrenceEndDatePicker = Flatpickr(
        editRecurrenceEndDatePickerRef.current,
        {
          dateFormat: "Y-m-d",
          minDate: formatDateGMT7(minDate),
          defaultDate: editingBooking.recurrenceEndDate,
          onChange: ([selectedDate]) => {
            if (selectedDate) {
              setEditingBooking((prev) => ({
                ...prev,
                recurrenceEndDate: formatDateGMT7(selectedDate),
              }));
            }
          },
        }
      );
    }

    if (editStartTimePickerRef.current && editingBooking) {
      Flatpickr(editStartTimePickerRef.current, {
        enableTime: true,
        noCalendar: true,
        dateFormat: "H:i",
        time_24hr: true,
        defaultDate: editingBooking.startTime,
        onChange: ([selectedTime]) => {
          if (selectedTime) {
            const timeStr = selectedTime.toTimeString().slice(0, 5);
            setEditingBooking((prev) => ({ ...prev, startTime: timeStr }));
          }
        },
      });
    }

    if (editEndTimePickerRef.current && editingBooking) {
      Flatpickr(editEndTimePickerRef.current, {
        enableTime: true,
        noCalendar: true,
        dateFormat: "H:i",
        time_24hr: true,
        defaultDate: editingBooking.endTime,
        onChange: ([selectedTime]) => {
          if (selectedTime) {
            const timeStr = selectedTime.toTimeString().slice(0, 5);
            setEditingBooking((prev) => ({ ...prev, endTime: timeStr }));
          }
        },
      });
    }

    return () => {
      if (editRecurrenceEndDatePicker) {
        editRecurrenceEndDatePicker.destroy();
      }
    };
  }, [editingBooking]);

  const createRoom = async () => {
    if (!newRoom.trim()) {
      showNotification("Room name cannot be empty", "danger");
      return;
    }
    try {
      await addDoc(collection(db, "rooms"), { name: newRoom.trim() });
      setNewRoom("");
      fetchRooms();
      showNotification("Room created successfully");
    } catch (error) {
      showNotification("Error creating room", "danger");
      console.error("Error creating room:", error);
    }
  };

  const updateRoom = async (id) => {
    if (!editedRoomName.trim()) {
      showNotification("Room name cannot be empty", "danger");
      return;
    }
    try {
      await updateDoc(doc(db, "rooms", id), { name: editedRoomName.trim() });
      const bookingsRef = collection(db, "rooms", id, "bookings");
      const bookingsSnapshot = await getDocs(bookingsRef);
      const updatePromises = bookingsSnapshot.docs.map(async (bookingDoc) => {
        await updateDoc(doc(db, "rooms", id, "bookings", bookingDoc.id), {
          room: editedRoomName.trim(),
        });
      });
      await Promise.all(updatePromises);
      await Promise.all([fetchRooms(), fetchBookings()]);
      setEditingRoomId(null);
      showNotification("Room and associated bookings updated successfully");
    } catch (error) {
      showNotification("Error updating room and bookings", "danger");
      console.error("Error updating room and bookings:", error);
    }
  };

  const deleteRoom = async (id) => {
    try {
      const bookingsRef = collection(db, "rooms", id, "bookings");
      const bookingsSnapshot = await getDocs(bookingsRef);
      const deletePromises = bookingsSnapshot.docs.map(async (bookingDoc) => {
        await deleteDoc(doc(db, "rooms", id, "bookings", bookingDoc.id));
      });
      await Promise.all(deletePromises);
      await deleteDoc(doc(db, "rooms", id));
      await Promise.all([fetchRooms(), fetchBookings()]);
      showNotification("Room and all associated bookings deleted successfully");
    } catch (error) {
      showNotification("Error deleting room and bookings", "danger");
      console.error("Error deleting room and bookings:", error);
    }
  };

  const deleteBooking = async (id, roomId) => {
    try {
      const bookingRef = doc(db, "rooms", roomId, "bookings", id);
      await deleteDoc(bookingRef);
      await sendBookingEmails(
        {
          ...itemToDelete,
          room: itemToDelete.room,
          email: "noreply@kambria.io", // since there's no email stored
          name: itemToDelete.name || "Admin",
        },
        "deleted",
        currentAdminEmail
      );
      await Promise.all([fetchBookings(), fetchRooms()]);
      showNotification("Booking deleted successfully");
    } catch (error) {
      showNotification("Error deleting booking", "danger");
      console.error("Error deleting booking:", error);
    }
  };

  const addAdminEmail = async () => {
    if (!newEmail.trim()) {
      showNotification("Email cannot be empty", "danger");
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newEmail.trim())) {
      showNotification("Invalid email format", "danger");
      return;
    }
    if (staticAllowedEmails.includes(newEmail.trim())) {
      showNotification(
        "This email is a reserved admin email and cannot be added",
        "danger"
      );
      return;
    }
    setIsAddingEmail(true);
    try {
      await setDoc(doc(db, "allowedEmails", newEmail.trim()), {
        addedAt: new Date().toISOString(),
      });
      setNewEmail("");
      await fetchAllowedEmails();
      showNotification("Admin email added successfully");
    } catch (error) {
      showNotification("Error adding admin email", "danger");
      console.error("Error adding admin email:", error);
    } finally {
      setIsAddingEmail(false);
    }
  };

  const deleteAdminEmail = async (email) => {
    try {
      await deleteDoc(doc(db, "allowedEmails", email));
      await fetchAllowedEmails();
      showNotification("Admin email removed successfully");
    } catch (error) {
      showNotification("Error removing admin email", "danger");
      console.error("Error removing admin email:", error);
    }
  };

  const handleDeleteClick = (item, type, name) => {
    setItemToDelete(item);
    setItemType(type);
    setItemName(name);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (itemType === "room") {
      await deleteRoom(itemToDelete.id);
    } else if (itemType === "booking") {
      await deleteBooking(itemToDelete.id, itemToDelete.roomId);
    } else if (itemType === "user") {
      await deleteAdminEmail(itemToDelete.id);
    }
    setShowDeleteModal(false);
    setItemToDelete(null);
    setItemType("");
    setItemName("");
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (type === "checkbox" && name === "isRecurring") {
      setNewBooking((prev) => ({ ...prev, isRecurring: checked }));
    } else if (name === "recurrenceDays") {
      setNewBooking((prev) => {
        const dayId = parseInt(value);
        const updatedDays = prev.recurrenceDays.includes(dayId)
          ? prev.recurrenceDays.filter((id) => id !== dayId)
          : [...prev.recurrenceDays, dayId];
        return { ...prev, recurrenceDays: updatedDays };
      });
    } else {
      setNewBooking((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleEditInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (type === "checkbox" && name === "isRecurring") {
      setEditingBooking((prev) => ({ ...prev, isRecurring: checked }));
    } else if (name === "recurrenceDays") {
      setEditingBooking((prev) => {
        const dayId = parseInt(value);
        const updatedDays = prev.recurrenceDays.includes(dayId)
          ? prev.recurrenceDays.filter((id) => id !== dayId)
          : [...prev.recurrenceDays, dayId];
        return { ...prev, recurrenceDays: updatedDays };
      });
    } else {
      setEditingBooking((prev) => ({ ...prev, [name]: value }));
    }
  };

  const resetNewBookingForm = () => {
    setNewBooking({
      room: "",
      date: "",
      startTime: "",
      endTime: "",
      name: "",
      purpose: "",
      isRecurring: false,
      recurrenceType: "weekly",
      recurrenceEndDate: "",
      recurrenceDays: [],
    });

    if (newDatePickerRef.current && newDatePickerRef.current._flatpickr) {
      newDatePickerRef.current._flatpickr.clear();
    }
    if (
      newStartTimePickerRef.current &&
      newStartTimePickerRef.current._flatpickr
    ) {
      newStartTimePickerRef.current._flatpickr.clear();
    }
    if (newEndTimePickerRef.current && newEndTimePickerRef.current._flatpickr) {
      newEndTimePickerRef.current._flatpickr.clear();
    }
    if (
      newRecurrenceEndDatePickerRef.current &&
      newRecurrenceEndDatePickerRef.current._flatpickr
    ) {
      newRecurrenceEndDatePickerRef.current._flatpickr.clear();
    }
  };

  const createBooking = async (e) => {
    e.preventDefault();
    setIsCreatingBooking(true);
    try {
      const {
        room,
        date,
        startTime,
        endTime,
        name,
        purpose,
        isRecurring,
        recurrenceType,
        recurrenceEndDate,
        recurrenceDays,
      } = newBooking;

      if (!room || !date || !startTime || !endTime || !name || !purpose) {
        showNotification(
          "Please fill in all required booking details",
          "danger"
        );
        setIsCreatingBooking(false);
        return;
      }

      // Validate that the booking date and time are not in the past
      const currentTime = moment.tz("Asia/Ho_Chi_Minh"); // Current time in GMT+7
      const bookingDateTime = moment.tz(
        `${date} ${startTime}`,
        "YYYY-MM-DD HH:mm",
        "Asia/Ho_Chi_Minh"
      );

      if (bookingDateTime.isBefore(currentTime)) {
        showNotification(
          "Booking date and time cannot be in the past",
          "danger"
        );
        setIsCreatingBooking(false);
        return;
      }

      const selectedRoom = rooms.find((r) => r.name === room);
      if (!selectedRoom) {
        showNotification("Selected room does not exist", "danger");
        setIsCreatingBooking(false);
        return;
      }
      const roomId = selectedRoom.id;

      if (isRecurring) {
        const start = new Date(date);
        const end = new Date(recurrenceEndDate);
        if (!recurrenceEndDate || isNaN(end) || end <= start) {
          showNotification(
            "Recurrence end date must be after the start date",
            "danger"
          );
          setIsCreatingBooking(false);
          return;
        }
        if (recurrenceType === "weekly" && recurrenceDays.length === 0) {
          showNotification(
            "Please select at least one day for weekly recurrence",
            "danger"
          );
          setIsCreatingBooking(false);
          return;
        }
      }

      try {
        await checkTimeSlotAvailability(newBooking, roomId, db);
      } catch (err) {
        showNotification(err.message, "danger");
        setIsCreatingBooking(false);
        return;
      }

      const roomRef = doc(db, "rooms", roomId);
      const bookingsRef = collection(roomRef, "bookings");
      const bookingData = {
        name,
        date,
        startTime,
        endTime,
        purpose,
        isRecurring,
        recurrenceType: isRecurring ? recurrenceType : null,
        recurrenceEndDate: isRecurring ? recurrenceEndDate : null,
        recurrenceDays: isRecurring ? recurrenceDays : [],
      };

      await addDoc(bookingsRef, bookingData);
      await Promise.all([fetchBookings(), fetchRooms()]);
      await sendBookingEmails(
        {
          ...bookingData,
          email: "noreply@kambria.io",
          name: newBooking.name,
          room: room,
        },
        "created",
        currentAdminEmail
      );
      resetNewBookingForm();
      showNotification("Booking created successfully");
    } catch (error) {
      showNotification("Error creating booking", "danger");
      console.error("Error creating booking:", error);
    } finally {
      setIsCreatingBooking(false);
    }
  };

  const updateBooking = async (e) => {
    e.preventDefault();
    setIsUpdatingBooking(true);
    try {
      // Validate that the booking date and time are not in the past
      const currentTime = moment.tz("Asia/Ho_Chi_Minh"); // Current time in GMT+7
      const bookingDateTime = moment.tz(
        `${editingBooking.date} ${editingBooking.startTime}`,
        "YYYY-MM-DD HH:mm",
        "Asia/Ho_Chi_Minh"
      );

      if (bookingDateTime.isBefore(currentTime)) {
        showNotification(
          "Booking date and time cannot be in the past",
          "danger"
        );
        setIsUpdatingBooking(false);
        return;
      }

      if (editingBooking.isRecurring) {
        const startDate = new Date(editingBooking.date);
        const endDate = new Date(editingBooking.recurrenceEndDate);
        if (!editingBooking.recurrenceEndDate || endDate <= startDate) {
          showNotification(
            "Recurrence end date must be after the start date",
            "danger"
          );
          setIsUpdatingBooking(false);
          return;
        }
        if (
          editingBooking.recurrenceType === "weekly" &&
          editingBooking.recurrenceDays.length === 0
        ) {
          showNotification(
            "Please select at least one day for weekly recurrence",
            "danger"
          );
          setIsUpdatingBooking(false);
          return;
        }
      }

      // Compare current editingBooking with original
      const originalBooking = bookings.find(
        (b) => b.id === editingBooking.id && b.roomId === editingBooking.roomId
      );

      if (originalBooking) {
        const isSame =
          originalBooking.name === editingBooking.name &&
          originalBooking.date === editingBooking.date &&
          originalBooking.startTime === editingBooking.startTime &&
          originalBooking.endTime === editingBooking.endTime &&
          originalBooking.purpose === editingBooking.purpose &&
          originalBooking.isRecurring === editingBooking.isRecurring &&
          originalBooking.recurrenceType === editingBooking.recurrenceType &&
          originalBooking.recurrenceEndDate ===
            editingBooking.recurrenceEndDate &&
          JSON.stringify(originalBooking.recurrenceDays || []) ===
            JSON.stringify(editingBooking.recurrenceDays || []);

        if (isSame) {
          showNotification("No changes detected", "warning");
          setIsUpdatingBooking(false);
          return;
        }
      }

      // Time conflict check
      try {
        await checkTimeSlotAvailability(
          editingBooking,
          editingBooking.roomId,
          db,
          editingBooking.id
        );
      } catch (err) {
        showNotification(err.message, "danger");
        setIsUpdatingBooking(false);
        return;
      }

      const bookingRef = doc(
        db,
        "rooms",
        editingBooking.roomId,
        "bookings",
        editingBooking.id
      );
      await updateDoc(bookingRef, {
        name: editingBooking.name,
        date: editingBooking.date,
        startTime: editingBooking.startTime,
        endTime: editingBooking.endTime,
        purpose: editingBooking.purpose,
        isRecurring: editingBooking.isRecurring,
        recurrenceType: editingBooking.isRecurring
          ? editingBooking.recurrenceType
          : null,
        recurrenceEndDate: editingBooking.isRecurring
          ? editingBooking.recurrenceEndDate
          : null,
        recurrenceDays: editingBooking.isRecurring
          ? editingBooking.recurrenceDays
          : [],
      });

      setEditingBooking(null);
      await Promise.all([fetchBookings(), fetchRooms()]);
      await sendBookingEmails(
        {
          ...editingBooking,
          room: editingBooking.room,
        },
        "updated",
        currentAdminEmail
      );
      resetEditing();
      showNotification("Booking updated successfully");
    } catch (error) {
      showNotification("Error updating booking", "danger");
      console.error("Error updating booking:", error);
    } finally {
      setIsUpdatingBooking(false);
    }
  };

  const startEditing = (booking) => {
    setEditingBooking({
      ...booking,
      date: new Date(booking.date).toISOString().split("T")[0],
      recurrenceEndDate: booking.recurrenceEndDate
        ? new Date(booking.recurrenceEndDate).toISOString().split("T")[0]
        : "",
      recurrenceDays: booking.recurrenceDays || [],
      recurrenceType: booking.recurrenceType || "weekly",
    });
  };

  const resetEditing = () => {
    setEditingBooking(null);

    if (newDatePickerRef.current && newDatePickerRef.current._flatpickr) {
      newDatePickerRef.current._flatpickr.clear();
    }
    if (
      newStartTimePickerRef.current &&
      newStartTimePickerRef.current._flatpickr
    ) {
      newStartTimePickerRef.current._flatpickr.clear();
    }
    if (newEndTimePickerRef.current && newEndTimePickerRef.current._flatpickr) {
      newEndTimePickerRef.current._flatpickr.clear();
    }
    if (
      newRecurrenceEndDatePickerRef.current &&
      newRecurrenceEndDatePickerRef.current._flatpickr
    ) {
      newRecurrenceEndDatePickerRef.current._flatpickr.clear();
    }

    // Clear Flatpickr fields
    if (editDatePickerRef.current && editDatePickerRef.current._flatpickr) {
      editDatePickerRef.current._flatpickr.clear();
    }
    if (
      editStartTimePickerRef.current &&
      editStartTimePickerRef.current._flatpickr
    ) {
      editStartTimePickerRef.current._flatpickr.clear();
    }
    if (
      editEndTimePickerRef.current &&
      editEndTimePickerRef.current._flatpickr
    ) {
      editEndTimePickerRef.current._flatpickr.clear();
    }
    if (
      editRecurrenceEndDatePickerRef.current &&
      editRecurrenceEndDatePickerRef.current._flatpickr
    ) {
      editRecurrenceEndDatePickerRef.current._flatpickr.clear();
    }
  };

  return (
    <AuthGuard>
      <Container className="admin-dashboard py-4">
        {notification && (
          <Alert
            variant={notification.variant}
            className="notification-alert"
            onClose={() => setNotification(null)}
            dismissible
          >
            {notification.message}
          </Alert>
        )}

        <h2 className="dashboard-title">Admin Dashboard</h2>

        <ConfirmDeleteModal
          show={showDeleteModal}
          onHide={() => setShowDeleteModal(false)}
          onConfirm={confirmDelete}
          itemType={itemType}
          itemName={itemName}
        />

        <Tabs
          activeKey={activeTab}
          onSelect={(k) => setActiveTab(k)}
          className="mb-4 admin-tabs"
        >
          <Tab eventKey="rooms" title="Manage Rooms">
            <Card className="mb-4 admin-card">
              <Card.Header className="card-header">Room Management</Card.Header>
              <Card.Body>
                <div className="room-form-container">
                  <Form.Control
                    placeholder="New room name"
                    value={newRoom}
                    onChange={(e) => setNewRoom(e.target.value)}
                    className="mb-3"
                  />
                  <Button onClick={createRoom} className="action-button">
                    Add
                  </Button>
                </div>

                <Table striped hover className="admin-table">
                  <thead>
                    <tr>
                      <th>Room Name</th>
                      <th>Booking Count</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rooms.map((room) => (
                      <tr key={room.id}>
                        <td>
                          {editingRoomId === room.id ? (
                            <Form.Control
                              value={editedRoomName}
                              onChange={(e) =>
                                setEditedRoomName(e.target.value)
                              }
                            />
                          ) : (
                            room.name
                          )}
                        </td>
                        <td>{bookingCounts[room.id] ?? "Loading..."}</td>
                        <td className="actions-cell">
                          {editingRoomId === room.id ? (
                            <>
                              <Button
                                size="sm"
                                variant="success"
                                onClick={() => updateRoom(room.id)}
                                className="me-2 action-button"
                              >
                                Save
                              </Button>
                              <Button
                                size="sm"
                                variant="outline-secondary"
                                onClick={() => setEditingRoomId(null)}
                                className="action-button"
                              >
                                Cancel
                              </Button>
                            </>
                          ) : (
                            <>
                              <Button
                                size="sm"
                                variant="outline-primary"
                                className="me-2 action-button"
                                onClick={() => {
                                  setEditingRoomId(room.id);
                                  setEditedRoomName(room.name);
                                }}
                              >
                                Edit
                              </Button>
                              <Button
                                size="sm"
                                variant="outline-danger"
                                onClick={() =>
                                  handleDeleteClick(room, "room", room.name)
                                }
                                className="action-button"
                              >
                                Delete
                              </Button>
                            </>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          </Tab>

          <Tab eventKey="bookings" title="Manage Bookings">
            {editingBooking ? (
              <Card className="mb-4 admin-card">
                <Card.Header className="card-header">Edit Booking</Card.Header>
                <Card.Body>
                  <Form className="booking-form" onSubmit={updateBooking}>
                    <Row>
                      <Col md={6}>
                        <Form.Select
                          name="room"
                          value={editingBooking.room}
                          onChange={handleEditInputChange}
                          className="mb-3"
                          disabled
                        >
                          <option value="">Select a room</option>
                          {rooms.map((room) => (
                            <option key={room.id} value={room.name}>
                              {room.name}
                            </option>
                          ))}
                        </Form.Select>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Control
                            type="text"
                            ref={editDatePickerRef}
                            placeholder="Select date"
                            value={editingBooking.date}
                            required
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Control
                            type="text"
                            ref={editStartTimePickerRef}
                            placeholder="Select start time"
                            required
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Control
                            type="text"
                            ref={editEndTimePickerRef}
                            placeholder="Select end time"
                            required
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                    <Row>
                      <Col md={6}>
                        <Form.Control
                          name="purpose"
                          placeholder="Purpose"
                          type="text"
                          value={editingBooking.purpose}
                          onChange={handleEditInputChange}
                          className="mb-3"
                          required
                        />
                      </Col>
                      <Col md={6}>
                        <Form.Control
                          name="name"
                          placeholder="Name"
                          value={editingBooking.name}
                          onChange={handleEditInputChange}
                          className="mb-3"
                          required
                        />
                      </Col>
                    </Row>
                    <Form.Group className="mb-3">
                      <Form.Check
                        type="checkbox"
                        id="edit-recurring-meeting"
                        label="Recurring Meeting"
                        name="isRecurring" // ← add this line
                        checked={editingBooking.isRecurring}
                        onChange={handleEditInputChange}
                      />
                    </Form.Group>
                    {editingBooking.isRecurring && (
                      <>
                        <Row>
                          <Col md={6}>
                            <Form.Group className="mb-3">
                              <Form.Label>Recurrence Type</Form.Label>
                              <Form.Select
                                name="recurrenceType"
                                value={editingBooking.recurrenceType}
                                onChange={handleEditInputChange}
                              >
                                <option value="daily">Daily</option>
                                <option value="weekly">Weekly</option>
                                <option value="monthly">Monthly</option>
                              </Form.Select>
                            </Form.Group>
                          </Col>
                          <Col md={6}>
                            <Form.Group className="mb-3">
                              <Form.Label>End Date</Form.Label>
                              <Form.Control
                                type="text"
                                ref={editRecurrenceEndDatePickerRef}
                                placeholder="Select end date"
                                required
                              />
                            </Form.Group>
                          </Col>
                        </Row>
                        {editingBooking.recurrenceType === "weekly" && (
                          <Form.Group className="mb-3">
                            <Form.Label>Repeat On</Form.Label>
                            <div className="day-selector">
                              {weekDays.map((day) => (
                                <Form.Check
                                  key={day.id}
                                  type="checkbox"
                                  id={`edit-day-${day.id}`}
                                  label={day.name}
                                  name="recurrenceDays"
                                  value={day.id}
                                  checked={editingBooking.recurrenceDays.includes(
                                    day.id
                                  )}
                                  onChange={handleEditInputChange}
                                  inline
                                />
                              ))}
                            </div>
                          </Form.Group>
                        )}
                      </>
                    )}
                    <div>
                      <Button
                        variant="success"
                        type="submit"
                        className="action-button me-2"
                        disabled={isUpdatingBooking}
                      >
                        {isUpdatingBooking ? "Saving..." : "Save Changes"}
                      </Button>
                      <Button
                        variant="outline-secondary"
                        onClick={resetEditing}
                        className="action-button"
                        disabled={isUpdatingBooking}
                      >
                        Cancel
                      </Button>
                    </div>
                  </Form>
                </Card.Body>
              </Card>
            ) : (
              <Card className="mb-4 admin-card">
                <Card.Header className="card-header">
                  Create New Booking
                </Card.Header>
                <Card.Body>
                  <Form className="booking-form" onSubmit={createBooking}>
                    <Row>
                      <Col md={6}>
                        <Form.Select
                          name="room"
                          value={newBooking.room}
                          onChange={handleInputChange}
                          className="mb-3 form-control"
                        >
                          <option value="">Select a room</option>
                          {rooms.map((room) => (
                            <option key={room.id} value={room.name}>
                              {room.name}
                            </option>
                          ))}
                        </Form.Select>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Control
                            type="text"
                            ref={newDatePickerRef}
                            placeholder="Select date"
                            value={newBooking.date}
                            required
                            disabled={newBooking.room === ""}
                            style={{
                              cursor: newBooking.room
                                ? "pointer"
                                : "not-allowed",
                            }}
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                    <Row>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Control
                            type="text"
                            ref={newStartTimePickerRef}
                            placeholder="Select start time"
                            required
                            disabled={newBooking.room === ""}
                            style={{
                              cursor: newBooking.room
                                ? "pointer"
                                : "not-allowed",
                            }}
                          />
                        </Form.Group>
                      </Col>
                      <Col md={6}>
                        <Form.Group className="mb-3">
                          <Form.Control
                            type="text"
                            ref={newEndTimePickerRef}
                            placeholder="Select end time"
                            required
                            disabled={newBooking.room === ""}
                            style={{
                              cursor: newBooking.room
                                ? "pointer"
                                : "not-allowed",
                            }}
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                    <Row>
                      <Col md={6}>
                        <Form.Control
                          name="purpose"
                          placeholder="Purpose"
                          type="text"
                          value={newBooking.purpose}
                          onChange={handleInputChange}
                          className="mb-3"
                          required
                          disabled={newBooking.room === ""}
                          style={{
                            cursor: newBooking.room ? "pointer" : "not-allowed",
                          }}
                        />
                      </Col>
                      <Col md={6}>
                        <Form.Control
                          name="name"
                          placeholder="Name"
                          value={newBooking.name}
                          onChange={handleInputChange}
                          className="mb-3"
                          required
                          disabled={newBooking.room === ""}
                          style={{
                            cursor: newBooking.room ? "pointer" : "not-allowed",
                          }}
                        />
                      </Col>
                    </Row>
                    <Form.Group className="mb-3">
                      <Form.Check
                        type="checkbox"
                        id="new-recurring-meeting"
                        label="Recurring Meeting"
                        name="isRecurring"
                        checked={newBooking.isRecurring}
                        onChange={handleInputChange}
                        disabled={newBooking.room === ""}
                        style={{
                          cursor: newBooking.room ? "pointer" : "not-allowed",
                        }}
                      />
                    </Form.Group>
                    {newBooking.isRecurring && (
                      <>
                        <Row>
                          <Col md={6}>
                            <Form.Group className="mb-3">
                              <Form.Label>Recurrence Type</Form.Label>
                              <Form.Select
                                name="recurrenceType"
                                value={newBooking.recurrenceType}
                                onChange={handleInputChange}
                                className="form-control"
                                disabled={newBooking.room === ""}
                                style={{
                                  cursor: newBooking.room
                                    ? "pointer"
                                    : "not-allowed",
                                }}
                              >
                                <option value="daily">Daily</option>
                                <option value="weekly">Weekly</option>
                                <option value="monthly">Monthly</option>
                              </Form.Select>
                            </Form.Group>
                          </Col>
                          <Col md={6}>
                            <Form.Group className="mb-3">
                              <Form.Label>End Date</Form.Label>
                              <Form.Control
                                type="text"
                                ref={newRecurrenceEndDatePickerRef}
                                placeholder="Select end date"
                                required={newBooking.isRecurring}
                                disabled={newBooking.room === ""}
                                style={{
                                  cursor: newBooking.room
                                    ? "pointer"
                                    : "not-allowed",
                                }}
                              />
                            </Form.Group>
                          </Col>
                        </Row>
                        {newBooking.recurrenceType === "weekly" && (
                          <Form.Group className="mb-3">
                            <Form.Label>Repeat On</Form.Label>
                            <div className="day-selector">
                              {weekDays.map((day) => (
                                <Form.Check
                                  key={day.id}
                                  type="checkbox"
                                  id={`new-day-${day.id}`}
                                  label={day.name}
                                  name="recurrenceDays"
                                  value={day.id}
                                  checked={newBooking.recurrenceDays.includes(
                                    day.id
                                  )}
                                  onChange={handleInputChange}
                                  inline
                                  disabled={newBooking.room === ""}
                                  style={{
                                    cursor: newBooking.room
                                      ? "pointer"
                                      : "not-allowed",
                                  }}
                                />
                              ))}
                            </div>
                          </Form.Group>
                        )}
                      </>
                    )}
                    <div>
                      <Button
                        variant="primary"
                        type="submit"
                        className="action-button"
                        disabled={isCreatingBooking}
                      >
                        {isCreatingBooking ? "Creating..." : "Create Booking"}
                      </Button>

                      <Button
                        variant="outline-secondary"
                        type="button"
                        onClick={resetNewBookingForm}
                        className="action-button ms-2"
                        disabled={isCreatingBooking}
                      >
                        Reset
                      </Button>
                    </div>
                  </Form>
                </Card.Body>
              </Card>
            )}

            <Card className="admin-card">
              <Card.Header className="card-header">All Bookings</Card.Header>
              <Card.Body>
                <Table striped hover responsive className="admin-table">
                  <thead>
                    <tr>
                      <th>Room</th>
                      <th>Date</th>
                      <th>Time</th>
                      <th>Name</th>
                      <th>Purpose</th>
                      <th>Recurrence</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bookings.map((b) => (
                      <tr key={b.id}>
                        <td>{b.room}</td>
                        <td>{new Date(b.date).toLocaleDateString("en-ca")}</td>
                        <td>
                          {b.startTime} - {b.endTime}
                        </td>
                        <td>{b.name}</td>
                        <td>{b.purpose}</td>
                        <td>{formatRecurrenceInfo(b)}</td>
                        <td className="actions-cell">
                          <Button
                            size="sm"
                            variant="outline-primary"
                            onClick={() => startEditing(b)}
                            className="action-button me-2"
                          >
                            Edit
                          </Button>
                          <Button
                            size="sm"
                            variant="outline-danger"
                            onClick={() =>
                              handleDeleteClick(b, "booking", b.purpose)
                            }
                            className="action-button"
                          >
                            Delete
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          </Tab>

          <Tab eventKey="users" title="Manage Users">
            <Card className="mb-4 admin-card">
              <Card.Header className="card-header">User Management</Card.Header>
              <Card.Body>
                <div className="room-form-container">
                  <Form.Control
                    placeholder="New admin email"
                    value={newEmail}
                    onChange={(e) => setNewEmail(e.target.value)}
                    className="mb-3"
                    type="email"
                  />
                  <Button
                    onClick={addAdminEmail}
                    className="action-button"
                    disabled={isAddingEmail}
                  >
                    {isAddingEmail ? "Adding..." : "Add"}
                  </Button>
                </div>

                <Table striped hover className="admin-table">
                  <thead>
                    <tr>
                      <th>Email</th>
                      <th>Added At</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {allowedEmails.map((user) => (
                      <tr key={user.id}>
                        <td>{user.id}</td>
                        <td>
                          {user.addedAt && isValid(new Date(user.addedAt))
                            ? format(
                                new Date(user.addedAt),
                                "yyyy-MM-dd HH:mm:ss"
                              )
                            : "N/A"}
                        </td>
                        <td className="actions-cell">
                          <Button
                            size="sm"
                            variant="outline-danger"
                            onClick={() =>
                              handleDeleteClick(user, "user", user.id)
                            }
                            className="action-button"
                          >
                            Remove
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          </Tab>
        </Tabs>
      </Container>
    </AuthGuard>
  );
};

export default AdminDashboard;
